import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlatform } from '../contexts/PlatformContext';
import { useToast } from '../contexts/ToastContext';
import { useNavigation } from '../contexts/NavigationContext';
import { connectApprovalWebSocket, disconnectApprovalWebSocket } from '../services/websocket';
import { Menu, X, LogOut, ChevronRight } from 'lucide-react';
// #182-followup — every nav section is gated by a `features.nav.<key>`
// flag (default ON). The /flags admin page exposes them grouped under
// "Navigation"; flipping one OFF hides that section everywhere.
import { isNavSectionEnabled } from '../utils/featureFlags';

/* ─── Navigation structure with section grouping ───
 *
 * Each top-level section AND each sub-group carries a `flagKey` that
 * maps to a `features.nav.<flagKey>` toggle in featureFlags.js. The
 * /flags admin page renders the toggles under a "Navigation" group;
 * flipping one OFF hides that section here at render time.
 */
const NAV_SECTIONS = [
  {
    label: 'Operations',
    flagKey: 'operations',
    items: [
      { id: 'overview', label: 'Overview', icon: '◎' },
      { id: 'agents', label: 'Agent Lifecycle', icon: '🤖' },
      { id: 'canvas', label: 'Workflow Canvas', icon: '◩' },
      { id: 'approvals', label: 'Approval Center', icon: '☑' },
    ],
  },
  {
    label: 'Manage',
    flagKey: 'manage',
    items: [
      { id: 'deployments', label: 'Organizations', icon: '⚡' },
      { id: 'connectors', label: 'Connectors', icon: '⤪' },
      { id: 'skills', label: 'Skill Center', icon: '✨' },
      { id: 'agent-marketplace', label: 'Agents', icon: '🏪' },
      { id: 'team', label: 'Team', icon: '👥' },
    ],
  },
  {
    label: 'Protection',
    flagKey: 'governance',
    items: [
      { id: 'protection-hub', label: 'Protection Hub', icon: '🛡' },
      { id: 'compliance', label: 'Compliance Center', icon: '📋' },
      // Workflow Audit — unified compliance trail spanning both archetypes
      // (Native Agentlets workflows AND External GEL Protected workflows).
      // Sits between Compliance Center (high-level rollup) and Policy Config
      // (rule authoring) — i.e., the per-run drill-in surface.
      { id: 'workflow-audit', label: 'Workflow Audit', icon: '🧾' },
      // Workflow Intelligence — analytics across thousands of runs (P50/P90/
      // P95/P99 latency, node hot-spots, soak / drift detection, governance
      // decision rates). Pairs with Workflow Audit (which shows individual
      // runs) — Intelligence answers "is this workflow healthy at scale?".
      { id: 'workflow-intelligence', label: 'Workflow Intelligence', icon: '📈' },
      { id: 'policy-config', label: 'Policy Config', icon: '⚙' },
    ],
    subGroups: [
      {
        label: 'Discovery & GEL',
        icon: '🔍',
        flagKey: 'discoveryGel',
        items: [
          { id: 'shadow-discovery', label: 'Agent Discovery', icon: '👁' },
          { id: 'workflow-discovery', label: 'Workflow Discovery', icon: '⚡' },
        ],
      },
      {
        // Sidebar group for handcrafted native agents. Renamed
        // 2026-05-04 to just "Lets" — the canonical base form in the
        // four-way Lets vocabulary (AutoLets / Lets / ExteLets /
        // SysLets). See constants/agentTypes.js.
        label: 'Lets',
        icon: '🤖',
        flagKey: 'agentletsAgents',
        items: [
          { id: 'native-trust', label: 'Trust Center', icon: '🎯' },
          { id: 'native-audit', label: 'Execution & Audit', icon: '📋' },
        ],
      },
      {
        // Formerly "External Agents". Renamed to ExteLets in the same
        // pass to match the Lets vocabulary across every UI surface.
        label: 'ExteLets',
        icon: '🌐',
        flagKey: 'externalAgents',
        items: [
          { id: 'external-trust', label: 'Trust Center', icon: '🎯' },
          { id: 'external-audit', label: 'Execution & Audit', icon: '📋' },
          { id: 'gel-agents', label: 'Registrations', icon: '📝' },
          { id: 'gel-connectors', label: 'Platform Connectors', icon: '🔌' },
        ],
      },
      {
        label: 'Protected Gateway',
        icon: '🔑',
        flagKey: 'governedGateway',
        items: [
          { id: 'gateway-keys', label: 'API Keys', icon: '🔐' },
          { id: 'gateway-dashboard', label: 'Gateway Dashboard', icon: '📊' },
        ],
      },
    ],
  },
  {
    label: 'Settings',
    flagKey: 'settings',
    items: [
      // Admin Center — single pane of glass for platform runtime ops
      // (Temporal OSS health, workflow operations, worker tuning, agent
      // inference, recovery). Sits at the top of Settings because it's
      // the operator-incident-response surface; everything else under
      // Settings is config-time tuning.
      // Note (2026-05-04): "Inference Controls" was removed from the
      // sidebar because Admin Center already includes the Agent
      // Inference tab.
      //
      // 2026-05-07 follow-up: the standalone /inference route is now
      // also gone from App.jsx (AdminCenterPage's embedded use of
      // InferencePage is the only consumer). The 'inference' entry
      // was correspondingly removed from ROLE_NAV below and from the
      // PATH_LABELS breadcrumb map further down — both were dead refs
      // pointing at a route that no longer exists.
      { id: 'admin-center', label: 'Admin Center', icon: '⚙' },
      { id: 'templates', label: 'Template Center', icon: '📋' },
    ],
  },
  {
    /* RC-89a — SYSTEM section is Platform Admin EXCLUSIVE.
     * Pre-RC-89a the V1 nav also carried `superAdminOnly: true`, which
     * inverted the RBAC hierarchy the same way the V2 nav did (Super
     * Admin saw Intelligence Center, Platform Admin did not). Both
     * layouts are reachable depending on the rebirth flag state, so
     * both had to be fixed. Per RBAC_RULE_MAP.md the SYSTEM section
     * and every item in it is Platform-Admin-only.
     * See [feedback_no_pa_sa_clubbing] and [reference_rbac_wildcard_scope]. */
    label: 'System · Platform Admin',
    flagKey: 'intelligence',
    platformAdminOnly: true,
    items: [
      { id: 'intelligence', label: 'Intelligence Center', icon: '🧠' },
    ],
  },
];

const ALL_ITEM_IDS = NAV_SECTIONS.flatMap(s => [
  ...s.items.map(i => i.id),
  ...(s.subGroups || []).flatMap(sg => sg.items.map(i => i.id)),
]);

const ROLE_NAV = {
  /* RC-89a — Super Admin's allowlist must NOT include 'intelligence'.
   * Intelligence Center is platform-wide (every customer) and belongs
   * to Platform Admin only. The previous spread `[...ALL_ITEM_IDS, 'intelligence']`
   * was double-wrong: intelligence was already in ALL_ITEM_IDS via the
   * SYSTEM section, and concatenating it again signaled the intent
   * that SA owned it. Both layers (section gate + per-id allowlist)
   * are now consistent. */
  'Super Admin': ALL_ITEM_IDS.filter(id => id !== 'intelligence'),
  'Platform Admin': ALL_ITEM_IDS,
  'Org Admin': ALL_ITEM_IDS.filter(id => id !== 'intelligence'),
  'Tenant Admin': [
    'overview', 'agents', 'canvas', 'approvals', 'connectors', 'skills', 'agent-marketplace', 'team',
    'admin-center', 'templates',
    'protection-hub', 'compliance', 'workflow-audit', 'workflow-intelligence',
    'policy-config', 'shadow-discovery', 'workflow-discovery',
    'native-trust', 'native-compliance', 'native-audit',
    'external-trust', 'external-compliance', 'external-audit', 'gel-agents', 'gel-connectors',
    'gateway-keys', 'gateway-dashboard',
  ],
  'Developer': ['overview', 'agents', 'canvas', 'approvals', 'connectors', 'skills', 'agent-marketplace'],
  'Viewer': ['overview', 'approvals'],
};

/* ─── Brand palette ─── */
const BRAND = {
  primary:    '#0d9488',
  dark:       '#0a766b',
  light:      '#e3f4f1',
  glow:       'rgba(226, 74, 58, 0.12)',
  glowStrong: 'rgba(226, 74, 58, 0.25)',
};

/* ─── Path → Label map for breadcrumb ─── */
const PATH_LABELS = {
  overview: 'Overview',
  agents: 'Agent Lifecycle',
  canvas: 'Workflow Canvas',
  approvals: 'Approval Center',
  deployments: 'Organizations',
  connectors: 'Connectors',
  skills: 'Skill Center',
  'agent-marketplace': 'Agents',
  team: 'Team',
  'admin-center': 'Admin Center',
  intelligence: 'Intelligence Center',
  templates: 'Template Center',
  'protection-hub': 'Governance Hub',
  'workflow-audit': 'Workflow Audit',
  'workflow-intelligence': 'Workflow Intelligence',
  'shadow-discovery': 'Agent Discovery',
  'workflow-discovery': 'Workflow Discovery & GEL',
  'native-trust': 'Trust Center — Agentlets',
  'native-compliance': 'Compliance — Agentlets',
  'native-audit': 'Execution & Audit — Agentlets',
  'policy-config': 'Policy Configuration',
  'external-trust': 'Trust Center — External',
  'external-compliance': 'Compliance — External',
  'external-audit': 'Execution & Audit — External',
  'gel-connectors': 'Platform Connectors',
  'gel-agents': 'Registrations — External',
  'gateway-keys': 'Gateway API Keys',
  'gateway-dashboard': 'Gateway Dashboard',
  // Legacy routes (kept for backwards compatibility)
  compliance: 'Compliance Center',
  trust: 'Agent Trust Center',
  'gel-dashboard': 'Audit Trail',
};

export default function DashboardLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, currentRole, tenant } = useAuth();
  const { approvalInboxCount, loadApprovalInboxCount } = usePlatform();
  const { addToast } = useToast();
  const { trail, currentLabel, currentContexts, navigateTrail } = useNavigation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Keys here MUST match the `label` field on each sidebar group above.
  // Updated 2026-05-04 alongside the Lets rename.
  const [expandedGroups, setExpandedGroups] = useState({ 'Discovery & GEL': true, 'Lets': true, 'ExteLets': true, 'Governed Gateway': true });
  const wsRef = useRef(null);

  // Poll approval inbox count every 60 seconds as fallback
  useEffect(() => {
    loadApprovalInboxCount();
    const interval = setInterval(loadApprovalInboxCount, 60000);
    return () => clearInterval(interval);
  }, [loadApprovalInboxCount]); // eslint-disable-line

  // Real-time WebSocket subscription for approval events
  const handleApprovalEvent = useCallback((event) => {
    // Refresh the badge count immediately
    loadApprovalInboxCount();

    // Show a toast notification based on event type
    const type = event?.type;
    const title = event?.title || event?.sourceName || 'Approval';
    if (type === 'APPROVAL_REQUESTED') {
      addToast(`New approval request: ${title}`, 'info');
    } else if (type === 'APPROVAL_ESCALATED') {
      addToast(`Escalated approval: ${title}`, 'warning');
    } else if (type === 'APPROVAL_APPROVED') {
      addToast(`Approved: ${title}`, 'success');
    } else if (type === 'APPROVAL_REJECTED') {
      addToast(`Rejected: ${title}`, 'error');
    } else if (type === 'APPROVAL_AWAITING_SECOND') {
      addToast(`Awaiting second approval: ${title}`, 'info');
    }
  }, [loadApprovalInboxCount, addToast]);

  useEffect(() => {
    const conn = connectApprovalWebSocket({
      onApprovalEvent: handleApprovalEvent,
    });
    wsRef.current = conn;
    return () => {
      disconnectApprovalWebSocket();
    };
  }, [handleApprovalEvent]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const allowedNav = ROLE_NAV[currentRole] || ['overview'];
  const currentPath = location.pathname.split('/').filter(Boolean)[0] || 'overview';

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#fafaf9' }}>
      {/* ═══ Sidebar ═══ */}
      <aside
        style={{
          width: sidebarOpen ? 260 : 0,
          background: '#ffffff',
          borderRight: '1px solid #e7e5e4',
          transition: 'width 0.25s cubic-bezier(.4,0,.2,1)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}
      >
        {/* ── Branded Header ── */}
        <div style={{
          background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.dark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Subtle decorative circles */}
          <div style={{
            position: 'absolute', top: -20, right: -20,
            width: 80, height: 80, borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
          }} />
          <div style={{
            position: 'absolute', bottom: -30, right: 40,
            width: 100, height: 100, borderRadius: '50%',
            background: 'rgba(255,255,255,0.03)',
          }} />

          <div style={{
            padding: '18px 20px',
            display: 'flex', alignItems: 'center', gap: 12,
            position: 'relative', zIndex: 1,
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(255,255,255,0.1)',
              overflow: 'hidden',
            }}>
              <img src="/brand/mark-dark.png" alt="Prove7 Control Vector Platform" style={{ width: 32, height: 'auto' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div>
              <h1 style={{
                fontSize: 17, fontWeight: 700, color: '#ffffff',
                margin: 0, lineHeight: 1.2, letterSpacing: '-0.01em',
              }}>
                agentlets
              </h1>
              <span style={{
                fontSize: 9, color: 'rgba(255,255,255,0.6)', fontWeight: 500,
                letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
                trusted autonomy
              </span>
            </div>
          </div>

          {/* Shimmer bar at bottom of branded header */}
          <div style={{
            height: 2,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.3) 100%)',
          }} />
        </div>

        {/* ── Section-Grouped Navigation ── */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 10px 4px' }}>
          {NAV_SECTIONS.map((section) => {
            /* RC-89a — Section gate must read the right role.
             * Platform Admin sections gate on currentRole === 'Platform Admin'
             * (strict, never `isSuperAdmin()` which historically conflates
             * PA + SA per the leak audit). Legacy `superAdminOnly` flag
             * is honored for back-compat with any section that genuinely
             * needs SA access, but the SYSTEM section flipped to
             * `platformAdminOnly`. */
            if (section.platformAdminOnly && currentRole !== 'Platform Admin') return null;
            if (section.superAdminOnly && currentRole !== 'Super Admin') return null;
            // Hide sections gated by an OFF nav flag (default ON when unset).
            if (section.flagKey && !isNavSectionEnabled(section.flagKey)) return null;
            const sectionItems = section.items.filter(item => allowedNav.includes(item.id));
            const subGroups = (section.subGroups || [])
              // Sub-groups have their own independent flag — Discovery & GEL,
              // Agentlets Agents, External Agents, Governed Gateway each
              // toggle separately under the parent Governance section.
              .filter(sg => !sg.flagKey || isNavSectionEnabled(sg.flagKey))
              .map(sg => ({
                ...sg,
                items: sg.items.filter(item => allowedNav.includes(item.id)),
              }))
              .filter(sg => sg.items.length > 0);
            if (sectionItems.length === 0 && subGroups.length === 0) return null;
            return (
              <div key={section.label} style={{ marginBottom: 4 }}>
                {/* Section label */}
                <div style={{
                  fontSize: 9, fontWeight: 600, color: '#a8a29e',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  padding: '10px 12px 6px',
                }}>
                  {section.label}
                </div>
                {sectionItems.map((item) => {
                  const isActive = currentPath === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(`/${item.id}`)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        marginBottom: 1,
                        borderRadius: 8,
                        border: (item.id === 'approvals' && approvalInboxCount > 0 && !isActive)
                          ? `1px solid ${BRAND.primary}44` : 'none',
                        background: isActive
                          ? BRAND.light
                          : (item.id === 'approvals' && approvalInboxCount > 0)
                            ? '#e3f4f1'
                            : 'transparent',
                        color: isActive
                          ? BRAND.primary
                          : (item.id === 'approvals' && approvalInboxCount > 0)
                            ? BRAND.primary
                            : '#78716c',
                        fontSize: 13,
                        fontWeight: isActive ? 600 : 450,
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        fontFamily: 'inherit',
                        transition: 'all 0.15s ease',
                        position: 'relative',
                        boxShadow: isActive ? `inset 0 0 0 1px ${BRAND.glow}` : 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.background = '#f5f5f4';
                          e.currentTarget.style.color = '#44403c';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#78716c';
                        }
                      }}
                    >
                      {/* Left edge active indicator (VS Code / dev-tool pattern) */}
                      {isActive && (
                        <div style={{
                          position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                          width: 3, height: 20, borderRadius: '0 3px 3px 0',
                          background: BRAND.primary,
                          boxShadow: `0 0 6px ${BRAND.glowStrong}`,
                        }} />
                      )}
                      <span style={{ fontSize: 15, width: 22, textAlign: 'center' }}>{item.icon}</span>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {/* Approval count badge — unmistakable red dot so
                          operators see at a glance there are pending
                          items waiting on them. We use a true red
                          (#dc2626) instead of BRAND.primary (which is a
                          coral/orange that doesn't read as "alert" when
                          glanced past). The pulse animation reinforces
                          urgency without being noisy. */}
                      {item.id === 'approvals' && approvalInboxCount > 0 && (
                        <span style={{
                          minWidth: 20, height: 20, borderRadius: 10,
                          background: '#dc2626', color: '#fff',
                          fontSize: 10, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: '0 6px', flexShrink: 0,
                          boxShadow: '0 0 8px rgba(220, 38, 38, 0.6)',
                          border: '1px solid rgba(255,255,255,0.85)',
                          animation: 'approvalPulse 2s ease-in-out infinite',
                        }}>
                          <style>{`
                            @keyframes approvalPulse {
                              0%, 100% { transform: scale(1);   box-shadow: 0 0 6px  rgba(220, 38, 38, 0.55); }
                              50%      { transform: scale(1.12); box-shadow: 0 0 14px rgba(220, 38, 38, 0.85); }
                            }
                          `}</style>
                          {approvalInboxCount > 99 ? '99+' : approvalInboxCount}
                        </span>
                      )}
                      {/* Active dot indicator */}
                      {isActive && (
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: BRAND.primary,
                          boxShadow: `0 0 6px ${BRAND.glowStrong}`,
                          flexShrink: 0,
                        }} />
                      )}
                    </button>
                  );
                })}

                {/* ── Expandable Sub-Groups (Governance section) ── */}
                {subGroups.map((sg) => {
                  const isExpanded = expandedGroups[sg.label] !== false;
                  const hasActiveChild = sg.items.some(i => currentPath === i.id);
                  return (
                    <div key={sg.label} style={{ marginTop: 2 }}>
                      <button
                        onClick={() => setExpandedGroups(prev => ({ ...prev, [sg.label]: !isExpanded }))}
                        style={{
                          width: '100%', padding: '8px 12px', borderRadius: 8, border: 'none',
                          background: hasActiveChild ? '#e3f4f1' : 'transparent',
                          color: hasActiveChild ? BRAND.primary : '#57534e',
                          fontSize: 12, fontWeight: 700, cursor: 'pointer', textAlign: 'left',
                          display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit',
                          letterSpacing: '0.02em',
                        }}
                        onMouseEnter={(e) => { if (!hasActiveChild) e.currentTarget.style.background = '#f5f5f4'; }}
                        onMouseLeave={(e) => { if (!hasActiveChild) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{sg.icon}</span>
                        <span style={{ flex: 1 }}>{sg.label}</span>
                        <ChevronRight size={12} style={{
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          opacity: 0.5,
                        }} />
                      </button>
                      {isExpanded && (
                        <div style={{ marginLeft: 16, borderLeft: `2px solid ${hasActiveChild ? BRAND.glow : '#e7e5e4'}`,
                                      paddingLeft: 0 }}>
                          {sg.items.map((subItem) => {
                            const isSubActive = currentPath === subItem.id;
                            return (
                              <button
                                key={subItem.id}
                                onClick={() => navigate(`/${subItem.id}`)}
                                style={{
                                  width: '100%', padding: '7px 12px', marginBottom: 0, borderRadius: 6,
                                  border: 'none',
                                  background: isSubActive ? BRAND.light : 'transparent',
                                  color: isSubActive ? BRAND.primary : '#78716c',
                                  fontSize: 12, fontWeight: isSubActive ? 600 : 400,
                                  cursor: 'pointer', textAlign: 'left', display: 'flex',
                                  alignItems: 'center', gap: 8, fontFamily: 'inherit',
                                  transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={(e) => { if (!isSubActive) { e.currentTarget.style.background = '#f5f5f4'; e.currentTarget.style.color = '#44403c'; } }}
                                onMouseLeave={(e) => { if (!isSubActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#78716c'; } }}
                              >
                                <span style={{ fontSize: 12, width: 18, textAlign: 'center' }}>{subItem.icon}</span>
                                <span style={{ flex: 1 }}>{subItem.label}</span>
                                {isSubActive && (
                                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: BRAND.primary, flexShrink: 0 }} />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* ── Sidebar Footer — Role & Tenant ── */}
        <div style={{ padding: '12px 10px 16px', borderTop: '1px solid #e7e5e4' }}>
          <div style={{
            padding: '10px 12px',
            background: '#fafaf9',
            borderRadius: 8,
            border: '1px solid #e7e5e4',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 5,
                background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.dark} 100%)`,
                color: '#ffffff',
                letterSpacing: '0.04em',
                boxShadow: `0 2px 6px ${BRAND.glowStrong}`,
              }}>
                <span style={{ fontSize: 5 }}>●</span>
                {currentRole}
              </span>
            </div>
            <div style={{
              fontSize: 11, color: '#78716c',
              fontFamily: "'SF Mono', 'Fira Code', monospace",
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {tenant}
            </div>
          </div>
        </div>
      </aside>

      {/* ═══ Main Content ═══ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* ── Top Bar with Breadcrumb ── */}
        <header
          style={{
            height: 56,
            background: '#ffffff',
            borderBottom: '1px solid #e7e5e4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: 16, paddingRight: 20,
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#a8a29e', padding: 8, display: 'flex', alignItems: 'center',
                borderRadius: 6, transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#1c1917'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#a8a29e'; }}
            >
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>

            {/* Breadcrumb Trail */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 0,
              fontSize: 13, color: '#a8a29e',
              overflow: 'hidden',
              minWidth: 0,
            }}>
              <span style={{ fontWeight: 500, flexShrink: 0 }}>agentlets</span>

              {/* Show previous trail entries as clickable breadcrumbs.
                  Cap at the 5 most-recent entries so the chain doesn't keep
                  accumulating across a long browsing session and collide with
                  the user-info block on the right. When truncated, show a
                  non-clickable "…" pill so users know history was hidden. */}
              {(() => {
                const MAX_PREV = 5;
                const previous = trail.slice(0, -1);
                const overflow = previous.length - MAX_PREV;
                const visiblePrevious = overflow > 0 ? previous.slice(-MAX_PREV) : previous;
                const indexOffset    = overflow > 0 ? overflow              : 0;
                return (
                  <>
                    {overflow > 0 && (
                      <>
                        <ChevronRight size={11} style={{ color: '#d6d3d1', flexShrink: 0, margin: '0 4px' }} />
                        <span
                          title={`${overflow} earlier ${overflow === 1 ? 'page' : 'pages'} hidden`}
                          style={{
                            fontWeight: 500, color: '#a8a29e',
                            whiteSpace: 'nowrap', flexShrink: 0,
                            padding: '0 4px',
                          }}
                        >
                          …
                        </span>
                      </>
                    )}
                    {visiblePrevious.map((entry, i) => (
                      <React.Fragment key={entry.path + (indexOffset + i)}>
                        <ChevronRight size={11} style={{ color: '#d6d3d1', flexShrink: 0, margin: '0 4px' }} />
                        <span
                          onClick={() => navigateTrail(indexOffset + i)}
                          style={{
                            fontWeight: 500, color: '#78716c', cursor: 'pointer',
                            whiteSpace: 'nowrap', flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                            maxWidth: 120,
                            transition: 'color 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#0d9488'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = '#78716c'; }}
                          title={entry.label}
                        >
                          {entry.label}
                        </span>
                      </React.Fragment>
                    ))}
                  </>
                );
              })()}

              {/* Current page */}
              <ChevronRight size={11} style={{ color: '#d6d3d1', flexShrink: 0, margin: '0 4px' }} />
              <span style={{ fontWeight: 600, color: '#292524', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {currentLabel}
              </span>

              {/* Sub-page context crumbs (e.g. agent name, workflow name, graph node) */}
              {currentContexts.map((ctx, i) => (
                <React.Fragment key={ctx.key + i}>
                  <ChevronRight size={11} style={{ color: '#d6d3d1', flexShrink: 0, margin: '0 4px' }} />
                  <span style={{
                    fontWeight: 500, color: '#0a766b', fontSize: 12,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    maxWidth: 160,
                  }} title={ctx.label}>
                    {ctx.label}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#292524', lineHeight: 1.2 }}>
                  {user?.name || 'User'}
                </div>
                <div style={{ fontSize: 11, color: '#a8a29e', lineHeight: 1.2 }}>
                  {user?.email}
                </div>
              </div>
              <div style={{
                width: 34, height: 34, borderRadius: 8,
                background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.dark} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: '#fff',
                letterSpacing: '0.02em',
                boxShadow: `0 2px 6px ${BRAND.glowStrong}`,
              }}>
                {user?.av || (user?.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
            </div>

            <div style={{ width: 1, height: 24, background: '#e7e5e4' }} />

            <button
              onClick={handleLogout}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#a8a29e', padding: 6, display: 'flex', alignItems: 'center',
                borderRadius: 6, transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.primary; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#a8a29e'; }}
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main style={{
          flex: 1, overflowY: 'auto',
          background: '#fafaf9',
        }}>
          {children}
        </main>
      </div>
    </div>
  );
}
